import { memo, useEffect, useRef, useState, ReactNode } from "react";
import { useLocation, useNavigate } from "react-router-dom";

import ArrowBackIosIcon from "@mui/icons-material/ArrowBackIos";
import Button from "@mui/material/Button";

import { useAppDispatch, useAppSelector } from "@zenith/app/hooks";
import {
  ButtonComponent,
  ModalComponent,
  SpinnerComponent,
  ToastSnackbarComponent,
} from "@zenith/components";
import { usePaymentsContext } from "@zenith/context/global-context/hooks";
import { PaymentsContextType } from "@zenith/context/global-context/types";
import {
  DEFAULT_CHANNEL,
  DEFAULT_CURRENCY,
  DEFAULT_CURRENCY_SYMBOL,
  DISCARD_PAYMENT_BODY,
  DISCARD_PAYMENT_PRIMARY_BTN_TEXT,
  DISCARD_PAYMENT_SECONDARY_BTN_TEXT,
  DISCARD_PAYMENT_TITLE,
  EXCEEDING_LIMITS_MODAL_BODY,
  EXCEEDING_LIMITS_MODAL_TITLE,
  EXPIRATION_DATE_FOR_BANK_TRANSFER_ITEM,
  EXPIRATION_DATE_FOR_QR_CODE_ITEM,
  NOT_NUMBERS_AND_DOT,
  ORDER_SUMMARY_MODAL_TITLE,
  PAYMENT_LINK_BASE_URL,
  STOCK_PRECHECK_ERROR_CODE,
  SUCCESS_API_CODE,
  UPGRADE_ACCOUNT_BUTTON,
} from "@zenith/data/constants";
import {
  emptyCart,
  selectCart,
} from "@zenith/features/Payments/components/ItemDetailsForm/item-details-form-component/cartSlice";
import { usePaymentIcon } from "@zenith/features/Payments/hooks/usePaymentIcon";
import { useNotificationContext } from "@zenith/context";
import { PaymentMethodsEnum } from "@zenith/features/Payments/types";
import {
  COLLECT_PAYMENT,
  DASHBOARD,
  MAKE_PAYMENT,
  PAYMENT_BANK_TRANSFER,
  PAYMENT_CONFIRM_TRANSFER,
  PAYMENT_PAYMENT_LINK,
  PAYMENT_PENDING_SALE,
  PAYMENT_QR_CODE,
  PAYMENT_SUCCESS,
  PAYMENT_USSD_CODE,
  UPGRADE_ACCOUNT,
} from "@zenith/routes/paths";
import {
  ApiResponseDTOOperationDTO,
  ApiResponseDTOOperationFullDTO,
  OperationDTO,
  OperationsApiService,
} from "@zenith/services/transactions";
import {
  ApiResponseDTOValidateOperationDTO,
  LimitsApiService,
} from "@zenith/services/user";
import {
  cleanLocalStorageInfo,
  formatCurrency,
  isExpirationDateValid,
} from "@zenith/utils";
import DiscardModalComponent from "@zenith/components/ui/DiscardModalComponent/discard-modal-component/discard-modal-component";
import ChevronLeft from "@zenith/assets/icons/ChevronLeft";
import StaticAccountPaymentMethod from "@zenith/assets/icons/components/StaticAccountPaymentMethod";
import StockPrecheckErrorModal, {
  StockErrorItem,
  parseStockError,
  parseStockErrorFromApiData,
  parseStockErrorItems,
} from "@zenith/features/CollectPayment/components/stock-precheck-error-modal/stock-precheck-error-modal";
import StaticAccountDisclaimer from "@zenith/features/CollectPayment/components/static-account-disclaimer/static-account-disclaimer";

interface OrderItem {
  id: number;
  image: string | undefined;
  name: string;
  quantity: number;
  price: string | undefined;
}

const PaymentOverviewPage = memo(function PaymentOverviewPage() {
  const { paymentsContext, setPaymentsContext, clearPaymentsContext } =
    usePaymentsContext();
  const {
    totalAmount = 0,
    amount = 0,
    totalItems = 0,
    orderItems = [],
    description = "Finopay payment collection",
  } = paymentsContext;
  const {
    transactionInfo: { ussdCode, accountNumber, qrCode, paymentLinkUrl },
  } = paymentsContext;

  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const paymentMethod = paymentsContext.paymentMethod;
  const paymentIcon = usePaymentIcon(paymentMethod);
  const ExceedingLimitsModal = ModalComponent;
  const OrderSummaryModal = ModalComponent;

  const [isLimitExceeded, setIsLimitExceeded] = useState<boolean>(false);
  const [limitExceededMessage, setLimitExceededMessage] =
    useState<ReactNode>("");
  const [accountLimit, setAccountLimit] = useState<number | null>(null);
  const [amountCollected, setAmountCollected] = useState<string | null>(null);
  const [balanceLeft, setBalanceLeft] = useState<string | null>(null);
  const [isOpenLimitModal, setIsOpenLimitModal] = useState<boolean>(false);
  const [exceedBalanceLimit, setExceedBalanceLimit] = useState<boolean>(false);
  const [percentageCollected, setPercentageCollected] = useState<string | null>(
    null
  );

  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [showStockErrorModal, setShowStockErrorModal] = useState(false);
  const [stockErrorItems, setStockErrorItems] = useState<StockErrorItem[]>([]);
  const [isAccountStatusLoading, setIsAccountStatusLoading] = useState(false);
  const [isDiscardModalOpen, setIsDiscardModalOpen] = useState<boolean>(false);
  const [isViewOtherItems, setIsViewOtherItems] = useState<boolean>(false);
  const { setOpen } = useNotificationContext();

  const [toasterData, setToasterData] = useState<{
    title: string;
    message: string;
    status: "success" | "error" | "warning";
  }>({
    title: "",
    message: "",
    status: "success",
  });

  function notify(
    title: string,
    message: string,
    status: "success" | "error" | "warning"
  ): void {
    setToasterData({
      title,
      message,
      status,
    });
    setOpen(true);
  }

  useEffect(() => {
    setOpen(false);
    if (paymentMethod === undefined) {
      navigate(MAKE_PAYMENT);
    }
  }, [paymentMethod, navigate]);

  // Cash and static account sales are created PENDING as soon as the merchant
  // reaches this summary (stock is reserved for the hour given by expiresAt),
  // then confirmed now or later.
  const isConfirmableSale =
    paymentMethod === PaymentMethodsEnum.CASH ||
    paymentMethod === PaymentMethodsEnum.STATIC_ACCOUNT;
  const [pendingSale, setPendingSale] = useState<OperationDTO>();
  const [isConfirmingSale, setIsConfirmingSale] = useState<boolean>(false);
  const hasRequestedSaleRef = useRef<boolean>(false);

  useEffect(() => {
    if (!isConfirmableSale || hasRequestedSaleRef.current) return;
    hasRequestedSaleRef.current = true;
    handleSubmit();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isConfirmableSale]);

  function getPaymentLinkUrl(operationId: string | undefined): string {
    return PAYMENT_LINK_BASE_URL + operationId;
  }

  function handleStockAwareError(error: any): void {
    const errorMessage: string = error?.body?.message || error?.message || "";
    if (error?.body?.internalCode === STOCK_PRECHECK_ERROR_CODE) {
      setStockErrorItems(
        errorMessage ? parseStockErrorItems(errorMessage, orderItems) : []
      );
      setShowStockErrorModal(true);
      return;
    }
    const apiData = error?.body?.data;
    if (Array.isArray(apiData) && apiData.length > 0) {
      setStockErrorItems(parseStockErrorFromApiData(apiData, orderItems));
      setShowStockErrorModal(true);
    } else if (/Insufficient stock for product/i.test(errorMessage)) {
      setStockErrorItems(parseStockError(errorMessage, orderItems));
      setShowStockErrorModal(true);
    } else {
      notify(
        "Request",
        errorMessage ||
          "An error occurred while processing request. Please try again.",
        "error"
      );
    }
  }

  const convertPriceToNumber = (price: string | undefined): number => {
    if (!price) return 0;
    const cleanedPrice = price.replace(/[^0-9.]/g, "");
    return parseFloat(cleanedPrice);
  };

  function getSaleItemDetail() {
    return (
      orderItems?.map((item: OrderItem) => ({
        amountPerUnit: convertPriceToNumber(item.price),
        description: item.name || "",
        quantity: item.quantity ? Number(item.quantity) : 1,
        saleItemId: item.id?.toString() || "0",
      })) || []
    );
  }

  function storePendingSale(operation: OperationDTO | undefined): void {
    setPendingSale(operation);
    setPaymentsContext({
      ...paymentsContext,
      totalAmount: operation?.totalAmount,
      expirationDate: operation?.expiresAt,
      operationId: operation?.operationId,
      transactionId: operation?.operationId,
      paymentStatus: operation?.status,
      operationDetails: operation,
      transactionInfo: {
        ...paymentsContext.transactionInfo,
        transactionDate: operation?.createdAt,
        paymentMethod: operation?.paymentMethod,
      },
    });
  }

  function handleCashPaymentMethod(): void {
    setIsLoading(true);

    if (totalItems > 0 && totalAmount > 0) {
      OperationsApiService.postOperationCash(DEFAULT_CHANNEL, {
        currency: DEFAULT_CURRENCY,
        saleItemDetail: getSaleItemDetail(),
      })
        .then((response: ApiResponseDTOOperationDTO) => {
          setIsLoading(false);
          if (response && response.internalCode === SUCCESS_API_CODE) {
            storePendingSale(response.data);
          } else {
            handleStockAwareError(response);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          handleStockAwareError(error);
        });
    } else {
      OperationsApiService.postOperationCash1(DEFAULT_CHANNEL, {
        amount: parseFloat(amount.toString().replace(NOT_NUMBERS_AND_DOT, "")),
        currency: DEFAULT_CURRENCY,
        description: description,
      })
        .then((response: ApiResponseDTOOperationDTO) => {
          setIsLoading(false);
          if (response && response.internalCode === SUCCESS_API_CODE) {
            storePendingSale(response.data);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          const errorMessage =
            error?.body?.message ||
            "An error occurred while processing request. Please try again.";
          notify("Request", errorMessage, "error");
        });
    }
  }

  function handleStaticAccountPaymentMethod(): void {
    setIsLoading(true);

    OperationsApiService.postOperationStaticAccount(DEFAULT_CHANNEL, {
      currency: DEFAULT_CURRENCY,
      saleItemDetail: getSaleItemDetail(),
    })
      .then((response: ApiResponseDTOOperationDTO) => {
        setIsLoading(false);
        if (response && response.internalCode === SUCCESS_API_CODE) {
          storePendingSale(response.data);
        } else {
          handleStockAwareError(response);
        }
      })
      .catch((error) => {
        setIsLoading(false);
        handleStockAwareError(error);
      });
  }

  function handleConfirmNow(): void {
    // The sale could not be created when the page opened: try again.
    if (!pendingSale?.operationId) {
      handleSubmit();
      return;
    }
    if (isConfirmingSale) return;

    const { operationId: saleId } = pendingSale;
    if (paymentMethod === PaymentMethodsEnum.STATIC_ACCOUNT) {
      navigate(PAYMENT_CONFIRM_TRANSFER, {
        state: {
          operationId: saleId,
          totalAmount: pendingSale.totalAmount ?? 0,
          sale: pendingSale,
        },
      });
      return;
    }

    setIsConfirmingSale(true);
    OperationsApiService.confirmSale(saleId)
      .then((response: ApiResponseDTOOperationDTO) => {
        setPaymentsContext({
          ...paymentsContext,
          paymentStatus: response.data?.status,
          operationDetails: response.data ?? pendingSale,
        });
        navigate(PAYMENT_SUCCESS, {
          replace: true,
          state: { operationId: saleId },
        });
      })
      .catch((error) => {
        if (error?.status === 422) {
          navigate(PAYMENT_PENDING_SALE, {
            replace: true,
            state: { isExpired: true },
          });
          return;
        }
        notify(
          "Request",
          error?.body?.message ||
            "We could not confirm this payment. Please try again.",
          "error"
        );
      })
      .finally(() => setIsConfirmingSale(false));
  }

  function handleConfirmLater(): void {
    clearContext(true);
    navigate(DASHBOARD, { replace: true });
  }

  function handleUSSDPaymentMethod(): void {
    setIsLoading(true);

    if (orderItems.length === 0) {
      navigate(PAYMENT_USSD_CODE, {
        replace: true,
        state: {
          amount: amount,
          currency: DEFAULT_CURRENCY,
          description: description,
        },
      });
    } else if (totalItems > 0 && totalAmount > 0) {
      const saleItemDetail =
        orderItems?.map((item: OrderItem) => ({
          amountPerUnit: convertPriceToNumber(item.price),
          description: item.name || "",
          quantity: item.quantity ? Number(item.quantity) : 1,
          saleItemId: item.id || 0,
        })) || [];

      navigate(PAYMENT_USSD_CODE, {
        replace: true,
        state: { saleItemDetail, amount: totalAmount },
      });
    } else {
      navigate(PAYMENT_USSD_CODE, {
        replace: true,
        state: {
          amount: parseFloat(
            totalAmount.toString().replace(NOT_NUMBERS_AND_DOT, "")
          ),
          currency: DEFAULT_CURRENCY,
          description: description,
        },
      });
    }
    setIsLoading(false);
  }

  function handleQRPaymentMethod(): void {
    const expDate = localStorage.getItem(EXPIRATION_DATE_FOR_QR_CODE_ITEM);
    setIsLoading(true);

    if (!isExpirationDateValid(expDate) || !paymentsContext.operationId) {
      if (totalItems > 0 && totalAmount > 0) {
        const saleItemDetail =
          orderItems?.map((item: OrderItem) => ({
            amountPerUnit: convertPriceToNumber(item.price),
            description: item.name || "",
            quantity: item.quantity ? Number(item.quantity) : 1,
            saleItemId: item.id?.toString() || "0",
          })) || [];

        OperationsApiService.postOperationQrCode(DEFAULT_CHANNEL, {
          currency: DEFAULT_CURRENCY,
          saleItemDetail,
        })
          .then((response: ApiResponseDTOOperationDTO) => {
            setIsLoading(false);
            if (response && response.internalCode === SUCCESS_API_CODE) {
              const expirationDate = response.data?.qrcode?.expirationDate;

              const paymentsCtx: PaymentsContextType = {
                ...paymentsContext,
                totalAmount: response.data?.totalAmount,
                expirationDate,
                operationId: response.data?.operationId,
                paymentStatus: response.data?.status,
                transactionInfo: {
                  ...paymentsContext.transactionInfo,
                  transactionDate: response.data?.createdAt,
                  paymentMethod: response.data?.paymentMethod,
                  qrCode: response.data?.qrcode?.operationCode,
                },
              };

              localStorage.setItem(
                EXPIRATION_DATE_FOR_QR_CODE_ITEM,
                expirationDate as string
              );

              setPaymentsContext(paymentsCtx);
              navigate(PAYMENT_QR_CODE, { replace: true });
            } else {
              handleStockAwareError(response);
            }
          })
          .catch((error) => {
            setIsLoading(false);
            handleStockAwareError(error);
          });
      } else {
        OperationsApiService.postOperationQrCode1(DEFAULT_CHANNEL, {
          amount: parseFloat(
            amount.toString().replace(NOT_NUMBERS_AND_DOT, "")
          ),
          currency: DEFAULT_CURRENCY,
          description: description,
        })
          .then((response: ApiResponseDTOOperationDTO) => {
            setIsLoading(false);
            if (response && response.internalCode === SUCCESS_API_CODE) {
              const expirationDate = response.data?.qrcode?.expirationDate;

              const paymentsCtx: PaymentsContextType = {
                ...paymentsContext,
                totalAmount: response.data?.totalAmount,
                expirationDate,
                operationId: response.data?.operationId,
                paymentStatus: response.data?.status,
                transactionInfo: {
                  ...paymentsContext.transactionInfo,
                  transactionDate: response.data?.createdAt,
                  paymentMethod: response.data?.paymentMethod,
                  qrCode: response.data?.qrcode?.operationCode,
                },
              };

              localStorage.setItem(
                EXPIRATION_DATE_FOR_QR_CODE_ITEM,
                expirationDate as string
              );

              setPaymentsContext(paymentsCtx);
              navigate(PAYMENT_QR_CODE, { replace: true });
            }
          })
          .catch((error) => {
            setIsLoading(false);
            const errorMessage =
              error?.body?.message ||
              "An error occurred while processing request. Please try again.";
            notify("Request", errorMessage, "error");
          });
      }
    } else {
      OperationsApiService.getOperationId(paymentsContext.operationId)
        .then((response: ApiResponseDTOOperationFullDTO) => {
          setIsLoading(false);
          if (response && response.internalCode === SUCCESS_API_CODE) {
            setPaymentsContext({
              ...paymentsContext,
              totalAmount: response.data?.totalAmount,
              expirationDate: response.data?.qrcode?.expirationDate,
              operationId: response.data?.operationId,
              transactionInfo: {
                ...paymentsContext.transactionInfo,
                transactionDate: response.dateTime,
                qrCode: response.data?.qrcode?.operationCode,
              },
            });

            navigate(PAYMENT_QR_CODE, { replace: true });
          }
        })
        .catch((error) => {
          setIsLoading(false);
          console.error(error.message);
          const errorMessage =
            error?.body?.message ||
            "An error occurred while processing request. Please try again.";
          notify("Request", errorMessage, "error");
        });
    }
  }

  function handleBankTransferPaymentMethod(): void {
    const expDate = localStorage.getItem(
      EXPIRATION_DATE_FOR_BANK_TRANSFER_ITEM
    );
    const url = PAYMENT_BANK_TRANSFER;

    setIsLoading(true);

    if (totalItems > 0 && totalAmount > 0) {
      const saleItemDetail =
        orderItems?.map((item: OrderItem) => ({
          amountPerUnit: convertPriceToNumber(item.price),
          description: item.name || "",
          quantity: item.quantity ? Number(item.quantity) : 1,
          saleItemId: item.id?.toString() || "0",
        })) || [];

      OperationsApiService.postOperationInBankTransfer(DEFAULT_CHANNEL, {
        currency: DEFAULT_CURRENCY,
        saleItemDetail,
      })
        .then((response: ApiResponseDTOOperationDTO) => {
          setIsLoading(false);
          if (response && response.internalCode === SUCCESS_API_CODE) {
            const expirationDate =
              response.data?.inBankTransfer?.expirationDate;

            const paymentsCtx: PaymentsContextType = {
              ...paymentsContext,
              totalAmount: response.data?.totalAmount,
              expirationDate,
              operationId: response.data?.operationId,
              paymentStatus: paymentsContext.paymentStatus,
              transactionInfo: {
                ...paymentsContext.transactionInfo,
                transactionDate: response.data?.createdAt,
                accountBank: response.data?.inBankTransfer?.bankName,
                accountName: response.data?.inBankTransfer?.accountName,
                accountNumber: response.data?.inBankTransfer?.accountNumber,
              },
            };

            localStorage.setItem(
              EXPIRATION_DATE_FOR_BANK_TRANSFER_ITEM,
              expirationDate as string
            );

            setPaymentsContext(paymentsCtx);

            navigate(url, { replace: true });
          } else {
            handleStockAwareError(response);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          handleStockAwareError(error);
        });
    } else {
      OperationsApiService.postOperationInBankTransfer1(DEFAULT_CHANNEL, {
        amount: parseFloat(amount.toString().replace(NOT_NUMBERS_AND_DOT, "")),
        currency: DEFAULT_CURRENCY,
        description: description,
      })
        .then((response: ApiResponseDTOOperationDTO) => {
          setIsLoading(false);
          if (response && response.internalCode === SUCCESS_API_CODE) {
            const expirationDate =
              response.data?.inBankTransfer?.expirationDate;

            const paymentsCtx: PaymentsContextType = {
              ...paymentsContext,
              totalAmount: response.data?.totalAmount,
              expirationDate,
              operationId: response.data?.operationId,
              paymentStatus: paymentsContext.paymentStatus,
              transactionInfo: {
                ...paymentsContext.transactionInfo,
                transactionDate: response.data?.createdAt,
                accountBank: response.data?.inBankTransfer?.bankName,
                accountName: response.data?.inBankTransfer?.accountName,
                accountNumber: response.data?.inBankTransfer?.accountNumber,
              },
            };

            localStorage.setItem(
              EXPIRATION_DATE_FOR_BANK_TRANSFER_ITEM,
              expirationDate as string
            );

            setPaymentsContext(paymentsCtx);

            navigate(url, { replace: true });
          }
        })
        .catch((error) => {
          setIsLoading(false);
          const errorMessage =
            error?.body?.message ||
            "An error occurred while processing request. Please try again.";
          notify("Request", errorMessage, "error");
        });
    }
  }

  function handlePaymentLinkPaymentMethod(): void {
    setIsLoading(true);

    if (totalItems > 0 && totalAmount > 0) {
      const saleItemDetail =
        orderItems?.map((item: OrderItem) => ({
          amountPerUnit: convertPriceToNumber(item.price),
          description: item.name || "",
          quantity: item.quantity ? Number(item.quantity) : 1,
          saleItemId: item.id?.toString() || "0",
        })) || [];

      OperationsApiService.postOperationPaymentLink(DEFAULT_CHANNEL, {
        currency: DEFAULT_CURRENCY,
        saleItemDetail,
      })
        .then((response: ApiResponseDTOOperationDTO) => {
          setIsLoading(false);
          if (response && response.internalCode === SUCCESS_API_CODE) {
            const operationId = response.data?.operationId;
            const paymentLinkUrl = getPaymentLinkUrl(operationId);

            const paymentsCtx: PaymentsContextType = {
              ...paymentsContext,
              totalAmount: response.data?.totalAmount,
              operationId,
              operationDetails: response.data,
              transactionInfo: {
                ...paymentsContext.transactionInfo,
                paymentLinkUrl,
                transactionDate: response.data?.createdAt,
              },
            };

            setPaymentsContext(paymentsCtx);
            navigate(PAYMENT_PAYMENT_LINK, {
              replace: true,
              state: {
                totalAmount: totalAmount,
              },
            });
          } else {
            handleStockAwareError(response);
          }
        })
        .catch((error) => {
          setIsLoading(false);
          handleStockAwareError(error);
        });
    } else {
      OperationsApiService.postOperationPaymentLink1(DEFAULT_CHANNEL, {
        amount: parseFloat(amount.toString().replace(NOT_NUMBERS_AND_DOT, "")),
        currency: DEFAULT_CURRENCY,
        description: description,
      })
        .then((response: ApiResponseDTOOperationDTO) => {
          setIsLoading(false);
          if (response && response.internalCode === SUCCESS_API_CODE) {
            const operationId = response.data?.operationId;
            const paymentLinkUrl = getPaymentLinkUrl(operationId);

            const paymentsCtx: PaymentsContextType = {
              ...paymentsContext,
              totalAmount: response.data?.totalAmount,
              operationId,
              operationDetails: response.data,
              transactionInfo: {
                ...paymentsContext.transactionInfo,
                paymentLinkUrl,
                transactionDate: response.data?.createdAt,
              },
            };

            setPaymentsContext(paymentsCtx);
            navigate(PAYMENT_PAYMENT_LINK, {
              replace: true,
              state: {
                amount: parseFloat(
                  amount.toString().replace(NOT_NUMBERS_AND_DOT, "")
                ),
              },
            });
          }
        })
        .catch((error) => {
          setIsLoading(false);
          const errorMessage =
            error?.body?.message ||
            "An error occurred while processing request. Please try again.";
          notify("Request", errorMessage, "error");
        });
    }
  }

  function clearContext(shouldEmptyCart?: boolean): void {
    clearPaymentsContext();
    cleanLocalStorageInfo();

    if (shouldEmptyCart) {
      dispatch(emptyCart());
    }
  }

  function handleSubmit(): void {
    // Prevent multiple clicks immediately
    if (isLoading) return;
    setIsLoading(true);

    // Disable confirm button immediately
    const confirmButton = document.querySelector(
      "#confirm-payment-button"
    ) as HTMLButtonElement;
    if (confirmButton) {
      confirmButton.disabled = true;
    }

    if (paymentMethod === PaymentMethodsEnum.CASH) {
      handleCashPaymentMethod();
    } else {
      const requestedAmount =
        totalItems > 0 && totalAmount > 0
          ? totalAmount
          : parseFloat(amount.toString().replace(NOT_NUMBERS_AND_DOT, ""));

      LimitsApiService.validateCreditOperation({
        requestedAmount,
      })
        .then((response: ApiResponseDTOValidateOperationDTO) => {
          if (response?.data?.willExceedLimits) {
            let formattedMessage: ReactNode = "Transaction limit exceeded.";

            if (response.data.message) {
              const messageParts = response.data.message.split(
                "Kindly upgrade your account to proceed with this collection"
              );

              if (
                response.data.amountCollected !== undefined &&
                response.data.balanceLeft !== undefined
              ) {
                const amountCollected =
                  response?.data?.amountCollected?.toString() || null;
                const balanceLeft =
                  response?.data?.balanceLeft?.toString() || null;

                setAmountCollected(amountCollected);
                setBalanceLeft(balanceLeft);
              }

              formattedMessage = (
                <div>
                  {messageParts}
                  <p className="pt-4 text-base font-semibold text-black">
                    Kindly upgrade your account to proceed with this collection.
                  </p>
                </div>
              );
            }

            if (response?.data?.willExceedBalanceLimit) {
              setExceedBalanceLimit(response.data.willExceedBalanceLimit);
            }

            if (response?.data?.percentageCollected) {
              const percentageCollected = response.data.percentageCollected;
              setPercentageCollected(percentageCollected);
            }

            setLimitExceededMessage(formattedMessage);
            setIsLimitExceeded(true);
            setIsAccountStatusLoading(true);

            LimitsApiService.getAccountStatus()
              .then((statusResponse) => {
                setAccountLimit(
                  statusResponse.data?.debit?.availableLimit ?? null
                );
              })
              .catch((error) => {
                const errorMessage =
                  error?.body?.message || "Failed to fetch account status";
                notify("Error", errorMessage, "error");
              })
              .finally(() => {
                setIsAccountStatusLoading(false);
                // Re-enable button only if limits are exceeded
                if (confirmButton) {
                  confirmButton.disabled = false;
                }
                setIsLoading(false);
              });
          } else {
            handlePayment();
          }
        })
        .catch((error) => {
          const errorMessage =
            error?.body?.message ||
            "An error occurred while validating the credit operation. Please try again.";
          notify("Validation Error", errorMessage, "error");
          // Re-enable button on error
          if (confirmButton) {
            confirmButton.disabled = false;
          }
          setIsLoading(false);
        });
    }
  }
  const salesSummary = paymentsContext.orderItems || [];

  const handleBackButton = () => {
    setPaymentsContext({
      ...paymentsContext,
      orderItems: salesSummary,
    });

    if (salesSummary.length === 0) {
      navigate(COLLECT_PAYMENT, {
        state: { activeTab: "enter-amount" },
      });
    } else {
      navigate(MAKE_PAYMENT);
    }
  };

  function handleExceedingLimitsModalRightButtonClick(): void {
    navigate(UPGRADE_ACCOUNT);
  }

  function handlePayment(): void {
    switch (paymentMethod) {
      case PaymentMethodsEnum.BANK_TRANSFER:
        handleBankTransferPaymentMethod();
        break;
      case PaymentMethodsEnum.CASH:
        handleCashPaymentMethod();
        break;
      case PaymentMethodsEnum.USSD:
        handleUSSDPaymentMethod();
        break;
      case PaymentMethodsEnum.QR_CODE_PAYMENT:
        handleQRPaymentMethod();
        break;
      case PaymentMethodsEnum.PAYMENT_LINK:
        handlePaymentLinkPaymentMethod();
        break;
      case PaymentMethodsEnum.STATIC_ACCOUNT:
        handleStaticAccountPaymentMethod();
        break;
      default:
        throw new Error("Payment Method not mapped");
    }
  }

  const closeLimitModal = () => {
    setIsOpenLimitModal(false);
  };

  const handleLeftButtonClick = () => {
    setIsOpenLimitModal(true);
    setIsDiscardModalOpen(true);
  };

  const handleDiscard = () => {
    clearContext(true);
    navigate(DASHBOARD);
    setIsDiscardModalOpen(false);
  };

  const handleCancelDiscard = () => {
    setIsDiscardModalOpen(false);
  };

  return (
    <div className="mb-3 flex-col items-start justify-between">
      <StockPrecheckErrorModal
        isOpen={showStockErrorModal}
        onClose={() => setShowStockErrorModal(false)}
        errorItems={stockErrorItems}
      />
      <ToastSnackbarComponent
        horizontal="center"
        status={toasterData.status}
        vertical="top"
        hasAutoHideDuration
        size="lg"
        title={toasterData.title}
        text={toasterData.message}
      />
      <OrderSummaryModal
        title={ORDER_SUMMARY_MODAL_TITLE}
        body={
          <div className="max-h-[320px] overflow-y-auto">
            {orderItems.map((item, index, arr) => (
              <div key={index} className="mx-4 mb-4">
                <div className="flex justify-between">
                  <p className="py-1 text-lg font-medium text-black">
                    {item.name}
                  </p>
                  <p className="text-base font-medium text-black">
                    {formatCurrency(
                      convertPriceToNumber(item.price) * item.quantity
                    )}
                  </p>
                </div>
                <div className="flex justify-between">
                  <p className="text-sm font-medium text-black">
                    {" "}
                    Quantity
                    <span className="ml-3">{item.quantity}</span>
                  </p>
                  <p className="text-sm font-medium text-black">
                    {" "}
                    Price per quantity
                    <span className="ml-3 text-xs font-semibold text-zinc-400">
                      {item.price}
                    </span>
                  </p>
                </div>
                {index !== arr.length - 1 && (
                  <hr className="my-4 h-px w-full border border-zinc-300" />
                )}
              </div>
            ))}
          </div>
        }
        showModal={isViewOtherItems}
        showCloseIcon={true}
        showErrorIcon={false}
        size="medium"
        onClickClose={() => setIsViewOtherItems(false)}
        hideButtons={true}
        spaceBetween={true}
        alignTitleStart={true}
      />
      <ExceedingLimitsModal
        title={EXCEEDING_LIMITS_MODAL_TITLE}
        body={limitExceededMessage || EXCEEDING_LIMITS_MODAL_BODY}
        secondLine={
          isAccountStatusLoading ? (
            <div className="flex justify-center pt-10">
              <SpinnerComponent type={"primary"} size={20} />
            </div>
          ) : accountLimit !== null && exceedBalanceLimit === true ? (
            <div className="flex-col px-4 pt-10">
              <div className="flex justify-between">
                <span className="text-xs text-[#9B9B9B]">Collected</span>
                <span className="text-xs text-[#9B9B9B]">Balance left</span>
              </div>
              <span className="flex py-4">
                <hr
                  className="border-t-2 border-primary-250"
                  style={{ width: `${percentageCollected}%`, float: "left" }}
                />
                <hr
                  className="border-t-2 border-gray-300"
                  style={{
                    width: `${100 - Number(percentageCollected ?? 0)}%`,
                    float: "right",
                  }}
                />
              </span>
              <div className="flex justify-between">
                <span className="font-bold text-black">
                  {amountCollected !== null
                    ? formatCurrency(amountCollected)
                    : formatCurrency(0)}
                </span>
                <span className="font-bold text-black">
                  {balanceLeft !== null
                    ? formatCurrency(balanceLeft)
                    : formatCurrency(0)}
                </span>
              </div>
            </div>
          ) : null
        }
        showModal={isLimitExceeded}
        showCloseIcon={false}
        doubleOptions
        leftButtonText={DISCARD_PAYMENT_PRIMARY_BTN_TEXT}
        rightButtonText={UPGRADE_ACCOUNT_BUTTON}
        onLeftButtonClick={handleLeftButtonClick}
        onRightButtonClick={handleExceedingLimitsModalRightButtonClick}
        icon={"error"}
        size="semi-large"
      />
      {isDiscardModalOpen && (
        <DiscardModalComponent
          isOpen={isOpenLimitModal}
          onClose={closeLimitModal}
          primaryAction={handleDiscard}
          secondaryAction={handleCancelDiscard}
          title={DISCARD_PAYMENT_TITLE}
          bodyText={DISCARD_PAYMENT_BODY}
          primaryActionText={DISCARD_PAYMENT_PRIMARY_BTN_TEXT}
          secondaryActionText={DISCARD_PAYMENT_SECONDARY_BTN_TEXT}
        />
      )}

      <ButtonComponent
        label="Back"
        onClick={handleBackButton}
        id="back-to-payment-page"
        size="large"
        type="tertiary"
        iconStart={<ChevronLeft />}
        className="my-2"
      />
      <div className="m-auto mt-5 w-full rounded-[20px] border border-neutral-200 md:w-3/5">
        <div className=" rounded-2.5xl bg-white py-6 px-8">
          <div className=" flex-col justify-between">
            <h1 className="mb-8 text-2xl font-medium text-neutral-900">
              Payment Overview
            </h1>
            <h1 className="mb-4 text-xl font-medium text-neutral-900">
              Order Summary
            </h1>
          </div>
          <hr className="border-stone-300"></hr>
          {totalItems > 0 && totalAmount > 0 && orderItems.length > 0 ? (
            <>
              <div className="mt-6 flex justify-between">
                <p className=" text-m font-medium text-black">
                  {orderItems[0].name}
                </p>
                <p className="text-sm font-medium text-black">
                  {orderItems[0].price}
                </p>
              </div>
              {orderItems.length > 1 ? (
                <div className="mt-6 flex justify-between pb-5">
                  <p className="text-xs font-light text-slate-400">
                    + {orderItems.length - 1} Other items
                  </p>
                  <p
                    className="cursor-pointer text-xs font-light text-primary-300 underline"
                    onClick={() => setIsViewOtherItems(true)}
                  >
                    View all items{">>"}
                  </p>
                </div>
              ) : (
                <div className="mt-2 flex justify-between pb-5">
                  <p className="text-xs font-light text-slate-400">
                    Quantity: {orderItems[0].quantity}
                  </p>
                </div>
              )}
            </>
          ) : (
            <div className="h-fit overflow-y-auto">
              <div className="text-align-center flex justify-between py-6">
                <p className="font-normal text-neutral-900">{description}</p>
                <h3 className="text-main-200-925 font-normal">
                  {formatCurrency(amount)}
                </h3>
              </div>
            </div>
          )}
          <hr className=" h-px w-full border border-zinc-300"></hr>
          <div className="my-6 flex justify-between pb-5">
            <p className="text-m font-medium text-black">Total</p>
            <h3 className="text-main-200-925 text-xl font-medium">
              {totalAmount > 0 && orderItems.length > 0
                ? formatCurrency(totalAmount)
                : formatCurrency(amount)}
            </h3>
          </div>
          <div>
            <h1 className="text-2xl font-medium text-neutral-900">
              Payment Method
            </h1>
            {paymentMethod === PaymentMethodsEnum.STATIC_ACCOUNT ? (
              // The customer pays a static account sale by bank transfer.
              <div className="mt-4 flex items-center">
                <StaticAccountPaymentMethod />
                <p className="ml-4">Pay with Transfer</p>
              </div>
            ) : (
              <div className="mt-4 flex items-center">
                <div className="flex h-12 w-12 items-center justify-center rounded-lg bg-neutral-50 p-2">
                  {paymentIcon && <paymentIcon.icon />}
                </div>
                <p className="ml-4">{paymentIcon && paymentIcon.text}</p>
              </div>
            )}
          </div>
          {paymentMethod === PaymentMethodsEnum.STATIC_ACCOUNT && (
            <StaticAccountDisclaimer />
          )}
        </div>
      </div>
      {isConfirmableSale ? (
        <div className="m-auto mt-8 flex w-full flex-col gap-4 sm:flex-row md:w-3/5">
          <ButtonComponent
            label="Confirm Now"
            onClick={handleConfirmNow}
            id="confirm-now-button"
            size="large"
            type="primary"
            fullWidth
            disabled={isLoading || isConfirmingSale}
            isLoading={isLoading || isConfirmingSale}
          />
          <ButtonComponent
            label="Confirm Later"
            onClick={handleConfirmLater}
            id="confirm-later-button"
            size="large"
            type="secondary"
            fullWidth
            disabled={!pendingSale?.operationId || isConfirmingSale}
          />
        </div>
      ) : (
        <div className="mt-8 flex justify-center">
          <ButtonComponent
            label="Confirm"
            onClick={handleSubmit}
            id="confirm-button"
            size="large"
            type="primary"
            disabled={isLoading}
            isLoading={isLoading}
            className="px-24"
          />
        </div>
      )}
    </div>
  );
});

export default PaymentOverviewPage;
